import React, { type ReactElement, type ReactNode } from "react";
import { MessageSquare, Users, type LucideIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@ndd/ui";
import { Eyebrow } from "./eyebrow";
import { RAIL_TRANSITION } from "./shell-layout";

// The siderail's presentational parts: tooltips, icon columns, clipped labels,
// section labels and Soon items. A leaf module (`import React`, relative
// imports, no router, no queries) so any shell's rail can reuse them and
// `tsx --test` can render them (NO-754).

// Each item explains itself from the side once only the icon column is
// visible. The Tooltip wrapper is unconditional because swapping wrapper
// types on collapse would remount the item and skip its transitions.
export function SiderailTooltip({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: ReactElement;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {collapsed ? (
        <TooltipContent side="right" sideOffset={10} className="max-lg:hidden">
          {label}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
}

export function SiderailIcon({
  icon: Icon,
  className,
  iconClassName,
}: {
  icon: LucideIcon;
  className?: string;
  iconClassName?: string;
}): ReactElement {
  return (
    <span className={cn("grid size-10 shrink-0 place-items-center", className)}>
      <Icon aria-hidden="true" className={iconClassName ?? "size-[17px]"} />
    </span>
  );
}

// Labels clip and fade instead of wrapping so the width animation never
// reflows text; thin-state styles are lg-only because the mobile drawer
// always renders the expanded layout.
export function SiderailLabelClip({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <span
      className={cn(
        "min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-150",
        collapsed && "lg:opacity-0"
      )}
    >
      {children}
    </span>
  );
}

export function SiderailSectionLabel({
  label,
  count,
}: {
  label: string;
  count: string;
}): ReactElement {
  return (
    <div className="flex items-baseline justify-between px-2.5 pb-[5px] pt-3.5">
      <Eyebrow size="sm">{label}</Eyebrow>
      <span className="font-sans text-caption font-bold text-subtle">{count}</span>
    </div>
  );
}

export function SiderailSoonItem({
  icon: Icon,
  label,
  description,
  collapsed,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  collapsed: boolean;
}): ReactElement {
  return (
    <SiderailTooltip label={`${label} — Soon`} collapsed={collapsed}>
      <div
        className={cn(
          "flex h-14 rounded-lg transition-[height]",
          RAIL_TRANSITION,
          collapsed && "lg:h-10"
        )}
      >
        <span className="relative grid size-10 shrink-0 place-items-center">
          <Icon aria-hidden="true" className="size-[17px] text-subtle" />
          {/* Thin state: the dashed micro-dot stands in for the Soon pill. */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute right-1 top-[5px] size-[9px] rounded-full border-[1.5px] border-dashed border-sand-400 bg-muted opacity-0 transition-opacity duration-150",
              collapsed && "lg:opacity-100"
            )}
          />
        </span>
        <SiderailLabelClip collapsed={collapsed}>
          <span
            className={cn(
              "mt-2 flex h-5 items-center gap-2 transition-[margin-top]",
              RAIL_TRANSITION,
              collapsed && "lg:mt-0"
            )}
          >
            <span className="font-sans text-body-lg font-bold text-subtle brand:font-body brand:font-medium">
              {label}
            </span>
            <span className="rounded-full border border-dashed border-(color:--border-decorative,var(--border-strong)) px-2 py-px font-sans text-micro font-semibold uppercase tracking-[0.08em] text-subtle brand:font-mono brand:font-normal">
              Soon
            </span>
          </span>
          <span className="mt-px block truncate pr-2 text-xs leading-[18px] text-subtle">
            {description}
          </span>
        </SiderailLabelClip>
      </div>
    </SiderailTooltip>
  );
}

export function SiderailOwnRdSoonItems({
  collapsed,
}: {
  collapsed: boolean;
}): ReactElement {
  return (
    <>
      <SiderailSoonItem
        icon={Users}
        label="Patients"
        description="Everyone you see — history, notes, and metrics."
        collapsed={collapsed}
      />
      <SiderailSoonItem
        icon={MessageSquare}
        label="Chat"
        description="Ask about your patients and sessions."
        collapsed={collapsed}
      />
    </>
  );
}

/** Attribution, never possession: no parse, no constructed possessive. */
export function AppointmentsLabel({
  viewingOther,
  viewedRdName,
}: {
  viewingOther: boolean;
  viewedRdName: string | null;
}): ReactElement {
  if (!viewingOther) return <>My Appointments</>;

  return (
    <span className="flex min-w-0 flex-col">
      <span>Appointments</span>
      {viewedRdName ? (
        <span
          title={viewedRdName}
          className="truncate text-meta font-medium text-muted-foreground"
        >
          {viewedRdName}
        </span>
      ) : null}
    </span>
  );
}

/** A Today row: the Up next row's left accent, the open Appointment's fill. */
export function siderailTodayRowClass(upNext: boolean, active: boolean): string {
  return cn(
    "my-px flex items-center gap-2 rounded-lg border-l-[3px] py-2 pl-3 pr-2 transition-colors hover:bg-bark/[0.04]",
    upNext ? "border-l-info" : "border-l-transparent",
    active && "bg-accent hover:bg-accent"
  );
}

/** A Today row's time, name and Up next pill. */
export function SiderailTodayRowContent({
  time,
  name,
  upNext,
}: {
  time: string;
  name: string;
  upNext: boolean;
}): ReactElement {
  return (
    <>
      <span
        className={cn(
          "w-[60px] shrink-0 text-meta",
          upNext ? "font-semibold text-info" : "text-muted-foreground"
        )}
      >
        {time}
      </span>
      <span className="min-w-0 flex-1 truncate text-body font-medium text-foreground">
        {name}
      </span>
      {upNext ? (
        <span className="shrink-0 rounded-full bg-info-soft px-2 py-0.5 text-eyebrow font-medium text-info">
          Up next
        </span>
      ) : null}
    </>
  );
}
